import type { HarvestSessionId, HarvestStageId } from "@brewbase/db/types";
import type { PlannerModel } from "@/features/harvest-planner/use-planner-model";
import type { StageNavItem } from "@/components/nav/sidebar-store";

/**
 * The session's plan as a sidebar reads it.
 *
 * The planner is two-dimensional — rows are time, columns are lineage — and a
 * sidebar is a list. The projection that survives is the row axis: stages in the
 * order they will be done. What the column axis contributes is not depth but
 * coverage, since a stage groups its template across every arm and so sits on
 * the trunk almost always. Indenting by branch depth was measured against seven
 * real sessions and put every row at depth zero.
 *
 * So a row says how many streams it runs on, and the divisions between rows are
 * drawn where the plan actually divides. Both come off the same `PlannerModel`
 * the grid draws, which is what stops the two surfaces disagreeing again.
 */

/**
 * How a stage is addressed.
 *
 * Built from the stored id, so a link survives the stage being renamed and
 * steps being inserted above it — which the derived ids it replaces could not.
 */
export const stagePath = (
  sessionId: HarvestSessionId,
  stageId: HarvestStageId,
): string => `/harvests/${sessionId}/stages/${stageId}`;

/**
 * The stage a legacy `?page=` param names, or null if it names something else.
 * Only the redirect that translates saved links still reads this.
 */
export function parseStagePage(page: string | undefined): number | null {
  const match = page?.match(/^stage-(\d+)$/);
  return match ? Number(match[1]) : null;
}

export function buildStageNav(
  model: PlannerModel,
  sessionId: HarvestSessionId,
): StageNavItem[] {
  const { columns, rows, forks } = model;
  if (!rows.length) return [];

  // A letter is only unique inside its harvest — four harvests running the same
  // plan each have an arm `b`. With several present the code has to lead, or one
  // label names four different streams at once.
  const multiHarvest = new Set(columns.map((c) => c.harvestId)).size > 1;
  const armLabel = (letters: string, column: number): string => {
    const code = columns[column]?.harvestCode ?? "";
    if (!multiHarvest) return letters;
    return letters ? `${code}·${letters}` : code;
  };

  /**
   * Folded by where they sit and what they divide into, so the four identical
   * forks of a four-harvest session become one line carrying a ×4.
   *
   * The shape is the arm count and the streams the arms take, deliberately not
   * the letters. Letters are unique within a harvest and flat, so every fork
   * would carry a different set of them and nothing would ever fold — and two
   * harvests dividing the same way at the same step is one fact regardless of
   * what each one's arms ended up called.
   */
  const foldedByRow = new Map<number, StageNavItem[]>();
  const seen = new Map<string, Extract<StageNavItem, { kind: "fork" }>>();
  for (const fork of forks) {
    const shape = fork.arms.map((a) => a.material ?? "").join("|");
    const signature = `${fork.afterRow}|${fork.arms.length}|${shape}`;
    const already = seen.get(signature);
    if (already) {
      already.multiplicity += 1;
      continue;
    }
    const item: Extract<StageNavItem, { kind: "fork" }> = {
      kind: "fork",
      key: `fork-${signature}`,
      arms: fork.arms.map((a) => armLabel(a.letters, a.from)),
      multiplicity: 1,
    };
    seen.set(signature, item);
    if (!foldedByRow.has(fork.afterRow)) foldedByRow.set(fork.afterRow, []);
    foldedByRow.get(fork.afterRow)!.push(item);
  }

  // One column means the branch axis carries nothing, so a count on every row
  // would report the same "1" all the way down.
  const showStreams = columns.length > 1;

  const items: StageNavItem[] = [];
  // A fork above every row belongs at the top of the list, not after row -1.
  for (const fork of foldedByRow.get(-1) ?? []) items.push(fork);
  rows.forEach((row, index) => {
    items.push({
      kind: "stage",
      href: stagePath(sessionId, row.stageId),
      label: row.name,
      icon: row.icon,
      streams: showStreams
        ? row.cells.reduce((total, cell) => total + cell.span, 0)
        : null,
    });
    for (const fork of foldedByRow.get(index) ?? []) items.push(fork);
  });

  return items;
}
